import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import AdminLayout from "./AdminLayout";
import { useAppT, localizeStatus } from "../components/home/i18n";
import { CheckCircle2, X, ExternalLink, AlertTriangle } from "lucide-react";
import { AuthedImage, AuthedVideo, AuthedFileLink } from "../components/AuthedMedia";

const ext = (n) => (n && n.includes(".") ? n.split(".").pop().toLowerCase() : "");

function MediaPreview({ m }) {
  const e = ext(m.original_filename);
  const path = `/media/${m.id}/file`;
  if (["jpg", "jpeg", "png"].includes(e)) return <AuthedImage path={path} className="h-20 w-20 rounded-sm object-cover" />;
  if (["mp4", "mov"].includes(e)) return <AuthedVideo path={path} className="h-24 w-40 rounded-sm bg-black" />;
  return <AuthedFileLink path={path} className="inline-flex items-center gap-1 text-sm text-[#1F4A38] underline"><ExternalLink className="h-3.5 w-3.5" /> {m.original_filename}</AuthedFileLink>;
}

function DetailModal({ id, onClose, onDone, a }) {
  const [d, setD] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { API.get(`/admin/profiles/${id}`).then(({ data }) => setD(data)); }, [id]);

  const act = async (path, body) => {
    setBusy(true);
    try { await API.post(`/admin/profiles/${id}/${path}`, body); toast.success(a.adminCand.done); onDone(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pending = new Set(d?.pending_fields || []);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="admin-detail-modal">
      <div className="my-8 w-full max-w-3xl rounded-md bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[#E8E4DB] px-6 py-4">
          <div className="font-serif text-2xl text-[#173A2C]">{d?.alias} <span className="text-sm text-[#5C5855]">{localizeStatus(d?.status_display, a)}</span></div>
          <button onClick={onClose} data-testid="close-modal"><X className="h-5 w-5 text-[#5C5855]" /></button>
        </div>
        {!d ? <div className="flex items-center justify-center gap-2 p-10 text-[#5C5855]"><div className="h-5 w-5 animate-spin rounded-full border-2 border-[#1F4A38] border-t-transparent" /> {a.common.loading}</div> : (
          <div className="max-h-[70vh] overflow-y-auto thin-scroll p-6">
            {d.pending_fields?.length > 0 && (
              <div className="mb-4 flex items-center gap-2 rounded-md border border-[#D28E3D]/40 bg-[#D28E3D]/10 p-3 text-sm text-[#8a5a1f]">
                <AlertTriangle className="h-4 w-4" /> {a.adminCand.pendingChanges} {d.pending_fields.join(", ")}{a.adminCand.pendingChangesPost}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-sm border border-[#E8E4DB] p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8B3A3A]">{a.adminCand.privateAdmin}</div>
                <div className="mt-1 text-sm text-[#2C2A29]">{d.private?.legal_name}</div>
                <div className="text-sm text-[#5C5855]">{d.private?.phone}</div>
                <div className="text-sm text-[#5C5855]">{d.private?.exact_address}</div>
                <div className="text-sm text-[#5C5855]">{d.candidate?.email}</div>
              </div>
              <div className="rounded-sm border border-[#E8E4DB] p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#C4A46B]">{a.adminCand.recruiterVisible}</div>
                <div className={`mt-1 text-sm ${pending.has("headline") ? "font-semibold text-[#b0722a]" : "text-[#2C2A29]"}`}>{d.public?.headline || "—"}</div>
                <div className="text-sm text-[#5C5855]">{d.public?.broad_location} · {d.public?.experience_years ?? "?"} {a.common.years} · {d.public?.availability}</div>
                <div className="mt-1 flex flex-wrap gap-1">{d.professions.map((p) => <span key={p} className="rounded-full bg-[#1F4A38]/6 px-2 py-0.5 text-xs text-[#1F4A38]">{p}</span>)}</div>
              </div>
            </div>

            <div className="mt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#C4A46B]">{a.adminCand.roleSkills}</div>
              {Object.entries(d.role_skills || {}).map(([p, sk]) => (
                <div key={p} className="mt-1 text-sm"><b className="text-[#1F4A38]">{p}:</b> <span className="text-[#5C5855]">{sk.join(", ")}</span></div>
              ))}
            </div>

            <div className="mt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#C4A46B]">{a.adminCand.evidenceMedia}</div>
              <div className="mt-2 flex flex-wrap gap-3">
                {d.media.length === 0 && <span className="text-sm text-[#8a8580]">{a.adminCand.noFilesShort}</span>}
                {d.media.map((m) => (
                  <div key={m.id} className="rounded-sm border border-[#E8E4DB] p-2"><div className="mb-1 text-[11px] text-[#5C5855]">{m.kind}</div><MediaPreview m={m} /></div>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-sm border border-[#E8E4DB] p-3">
              <label className="text-xs font-medium text-[#5C5855]">{a.adminCand.reasonLabel}</label>
              <textarea data-testid="reason-input" rows="2" value={reason} onChange={(e) => setReason(e.target.value)}
                className="mt-1 w-full rounded-sm border border-[#E8E4DB] px-3 py-2 text-sm outline-none focus:border-[#1F4A38]" placeholder={a.adminCand.reasonPlaceholder} />
              <div className="mt-3 flex flex-wrap gap-2">
                <button data-testid="approve-btn" disabled={busy} onClick={() => act("approve", {})} className="inline-flex items-center gap-1 rounded-full bg-[#2E6B4B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><CheckCircle2 className="h-4 w-4" /> {a.adminCand.approve}</button>
                <button data-testid="request-changes-btn" disabled={busy} onClick={() => reason.trim() ? act("request-changes", { reason }) : toast.error(a.adminCand.addReason)} className="rounded-full border border-[#D28E3D] px-4 py-2 text-sm font-semibold text-[#b0722a] disabled:opacity-50">{a.adminCand.requestChanges}</button>
                {d.approved && <button data-testid="revoke-btn" disabled={busy} onClick={() => act("revoke", { reason: reason || "Approval revoked" })} className="rounded-full border border-[#8B3A3A] px-4 py-2 text-sm font-semibold text-[#8B3A3A] disabled:opacity-50">{a.adminCand.revokeApproval}</button>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminCandidates() {
  const { a } = useAppT();
  const [profiles, setProfiles] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("action");

  const load = async () => { const { data } = await API.get("/admin/profiles"); setProfiles(data.profiles); };
  useEffect(() => { load(); }, []);

  const needsAction = (p) => ["submitted", "under_review"].includes(p.review_status) || (p.approved && p.has_pending_changes);
  const shown = profiles.filter((p) => filter === "all" ? true : needsAction(p));

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-serif text-3xl text-[#173A2C]">{a.adminCand.title}</h1>
        <div className="flex gap-1 rounded-full border border-[#E8E4DB] bg-white p-1">
          <button data-testid="filter-action" onClick={() => setFilter("action")} className={`rounded-full px-3 py-1 text-sm ${filter === "action" ? "bg-[#1F4A38] text-white" : "text-[#5C5855]"}`}>{a.adminCand.needsAction}</button>
          <button data-testid="filter-all" onClick={() => setFilter("all")} className={`rounded-full px-3 py-1 text-sm ${filter === "all" ? "bg-[#1F4A38] text-white" : "text-[#5C5855]"}`}>{a.adminCand.all}</button>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-md border border-[#E8E4DB] bg-white thin-scroll">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead><tr className="border-b border-[#E8E4DB] bg-[#F4F1EA] text-[11px] uppercase tracking-wide text-[#5C5855]"><th className="px-5 py-3">{a.adminCand.colAlias}</th><th className="py-3">{a.adminCand.colCandidate}</th><th className="py-3">{a.adminCand.colProfessions}</th><th className="py-3">{a.adminCand.colStatus}</th><th className="py-3"></th></tr></thead>
          <tbody data-testid="admin-profiles-table">
            {shown.length === 0 ? <tr><td colSpan="5" className="px-5 py-8 text-center text-[#5C5855]">{a.common.nothingHere}</td></tr> :
              shown.map((p) => (
                <tr key={p.id} className="border-b border-[#E8E4DB] last:border-0">
                  <td className="px-5 py-3 font-semibold text-[#173A2C]">{p.alias}</td>
                  <td className="py-3 text-[#2C2A29]">{p.candidate?.name}<div className="text-[11px] text-[#5C5855]">{p.candidate?.email}</div></td>
                  <td className="py-3 text-[#5C5855]">{p.professions.join(", ") || "—"}</td>
                  <td className="py-3"><span className="text-[#2C2A29]">{localizeStatus(p.status_display, a)}</span></td>
                  <td className="py-3"><button data-testid={`review-${p.alias}`} onClick={() => setOpenId(p.id)} className="rounded-sm bg-[#1F4A38] px-3 py-1.5 text-xs font-semibold text-white">{a.adminCand.review}</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {openId && <DetailModal id={openId} onClose={() => setOpenId(null)} onDone={load} a={a} />}
    </AdminLayout>
  );
}
